import {
  CALLBACK_MESSAGE_CODES,
  CALLBACK_MESSAGE_GENERIC_ERROR_KEY,
  CALLBACK_MESSAGE_GENERIC_SUCCESS_KEY,
  SSO_ERROR_CODES,
  SSO_ERROR_GENERIC_KEY,
  SSO_WARNING_CODES,
} from "../constants/callback-message";
import type {
  ICallbackOutcome,
  IParseCallbackOutcomeProps,
  IReadParamProps,
  IResolveCallbackMessageKeyProps,
  IResolveSsoErrorKeyProps,
  IResolveSsoWarningKeyProps,
  TCallbackMessageKey,
  TSsoErrorMessageKey,
  TSsoWarningMessageKey,
} from "./callback-message.types";

const readParam = ({ query, name }: IReadParamProps): string | undefined => {
  const value = query[name];
  const first = Array.isArray(value) ? value[0] : value;
  return typeof first === "string" && first.length > 0 ? first : undefined;
};

// NOTE: NSAI-6112 — `Object.hasOwn`, not `code in`, so a crafted
// `messageKey=constructor` cannot reach a prototype member.
export const resolveCallbackMessageKey = ({
  code,
  isSuccess,
}: IResolveCallbackMessageKeyProps): TCallbackMessageKey => {
  const entry =
    code !== undefined && Object.hasOwn(CALLBACK_MESSAGE_CODES, code)
      ? CALLBACK_MESSAGE_CODES[code]
      : undefined;

  if (entry && entry.isSuccess === isSuccess) {
    return entry.key;
  }

  return isSuccess
    ? CALLBACK_MESSAGE_GENERIC_SUCCESS_KEY
    : CALLBACK_MESSAGE_GENERIC_ERROR_KEY;
};

// NOTE: NSAI-6112 — reads a callback redirect's outcome off its query. Returns
// null when the URL states no outcome, or states one without any message
// (a bare `?success=true` has never produced a toast). The legacy `message`
// param only signals THAT the server had something to say; its text is never
// returned, so it can never reach a renderer. `error=true` wins over
// `success=true` when a URL carries both.
export const parseCallbackOutcome = ({
  query,
}: IParseCallbackOutcomeProps): ICallbackOutcome | null => {
  const isError = readParam({ query, name: "error" }) === "true";
  const isSuccess =
    !isError && readParam({ query, name: "success" }) === "true";

  if (!isError && !isSuccess) {
    return null;
  }

  const code = readParam({ query, name: "messageKey" });
  const hasLegacyMessage = readParam({ query, name: "message" }) !== undefined;

  if (code === undefined && !hasLegacyMessage) {
    return null;
  }

  return {
    isSuccess,
    messageKey: resolveCallbackMessageKey({ code, isSuccess }),
  };
};

export const resolveSsoErrorKey = ({
  code,
}: IResolveSsoErrorKeyProps): TSsoErrorMessageKey =>
  typeof code === "string" && Object.hasOwn(SSO_ERROR_CODES, code)
    ? SSO_ERROR_CODES[code]
    : SSO_ERROR_GENERIC_KEY;

// NOTE: NSAI-6755 — null for anything that is not a listed warning code, so a
// forged `ssoWarning` param shows nothing at all.
export const resolveSsoWarningKey = ({
  code,
}: IResolveSsoWarningKeyProps): TSsoWarningMessageKey | null =>
  typeof code === "string" && Object.hasOwn(SSO_WARNING_CODES, code)
    ? SSO_WARNING_CODES[code]
    : null;
